"use client"

import { Download, Maximize2, Minimize2, Zap, Sparkles, Eye, Layers, Target, Info, Star, ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"

interface PreviewSectionProps {
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
}

export function AIPreviewSection({ isFullscreen = false, onToggleFullscreen }: PreviewSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const [activeInsight, setActiveInsight] = useState(0)

  const handleDownload = () => {
    console.log(" Downloading preview content")
    // In a real app, this would download the diagram/image
  }

  const insights = [
    {
      id: 1,
      icon: Sparkles,
      title: "Did you know?",
      content: "The Sun accounts for 99.86% of the mass in the entire solar system!",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50/50"
    },
    {
      id: 2,
      icon: Zap,
      title: "Fun Fact",
      content: "Jupiter's Great Red Spot is a storm that has raged for over 350 years.",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50/50"
    },
    {
      id: 3,
      icon: Target,
      title: "Quick Tip",
      content: "Saturn's rings are made of ice particles ranging from dust to boulder size.",
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-amber-50 to-orange-50/50"
    }
  ]

  const planets = [
    { name: "Mercury", size: 6, color: "bg-slate-400", position: "left-[60px] top-[45%]", orbit: 80 },
    { name: "Venus", size: 8, color: "bg-amber-300", position: "left-[100px] top-[30%]", orbit: 110 },
    { name: "Earth", size: 9, color: "bg-blue-400", position: "left-[140px] top-[55%]", orbit: 140, moon: true },
    { name: "Mars", size: 7, color: "bg-red-400", position: "left-[180px] top-[40%]", orbit: 170 },
    { name: "Jupiter", size: 20, color: "bg-orange-300", position: "left-[230px] top-[65%]", orbit: 220, rings: true },
    { name: "Saturn", size: 18, color: "bg-yellow-200", position: "left-[290px] top-[35%]", orbit: 280, rings: true },
  ]

  return (
    <section
      className={`relative overflow-hidden flex flex-col transition-all duration-500 ${
        isFullscreen 
          ? "col-span-2 w-full" 
          : "hidden xl:flex w-[420px]"
      } ${
        isFullscreen 
          ? "bg-gradient-to-br from-white via-white to-slate-50/30" 
          : "bg-gradient-to-b from-white to-slate-50/50"
      } border-l border-slate-100/50 shadow-lg`}
    >
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-teal-100/20 to-blue-100/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-100/10 to-pink-100/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />

      {/* Header */}
      <div className="relative z-10 h-16 flex items-center justify-between px-6 border-b border-slate-100/50 bg-white/30 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-sm">
            <Eye className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 text-lg tracking-tight">Visual Preview</h3>
            <p className="text-xs text-slate-500 font-medium">AI-generated insights</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={handleDownload}
            className="group p-2.5 hover:bg-white/80 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 border border-slate-200/50 bg-white/50 backdrop-blur-sm"
            title="Download"
          >
            <Download size={18} className="text-slate-600 group-hover:text-teal-600 transition-colors" />
          </button>
          <button
            onClick={onToggleFullscreen}
            className="group p-2.5 hover:bg-white/80 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 border border-slate-200/50 bg-white/50 backdrop-blur-sm"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 size={18} className="text-slate-600 group-hover:text-teal-600 transition-colors" />
            ) : (
              <Maximize2 size={18} className="text-slate-600 group-hover:text-teal-600 transition-colors" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 overflow-y-auto p-6 space-y-6">
        {/* Generated Diagram Card */}
        <div className="group relative bg-gradient-to-br from-white to-slate-50/50 border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
          {/* Card Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg">
                <Layers className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <span className="text-sm font-bold text-slate-900">Generated Diagram</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs text-slate-500 font-medium">Live visualization</span>
                </div>
              </div>
            </div>
            <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-xs font-semibold rounded-full border border-green-200">
              AI Generated
            </span>
          </div>

          {/* Diagram Visualization */}
          <div 
            className={`relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 rounded-xl overflow-hidden mb-6 transition-all duration-500 ${
              isZoomed ? "aspect-square" : "aspect-video"
            } ${isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            onClick={() => setIsZoomed(!isZoomed)}
          >
            {/* Stars Background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800/50 via-slate-900 to-slate-950">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-[1px] h-[1px] bg-white rounded-full animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    opacity: Math.random() * 0.8 + 0.2
                  }}
                />
              ))}
            </div>

            {/* Sun */}
            <div className="absolute -left-10 top-1/2 -translate-y-1/2">
              <div className="relative">
                <div className="w-36 h-36 rounded-full bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 blur-xl opacity-70 animate-pulse" />
                <div className="absolute inset-0 w-28 h-28 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="absolute inset-0 w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 to-orange-300 animate-pulse" style={{ animationDuration: '2s' }} />
              </div>
            </div>

            {/* Orbital Rings */}
            {[80, 110, 140, 170, 220, 280].map((radius, i) => (
              <div
                key={i}
                className="absolute border border-slate-600/20 rounded-full"
                style={{
                  width: `${radius * 2}px`,
                  height: `${radius * 2}px`,
                  left: '-20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  animation: `spin ${20 + i * 5}s linear infinite`
                }}
              />
            ))}

            {/* Planets */}
            {planets.map((planet, i) => (
              <div
                key={planet.name}
                className={`absolute ${planet.position} transition-all duration-300 group-hover:scale-110`}
                style={{
                  animation: `orbit ${30 + i * 10}s linear infinite`,
                  transformOrigin: '-20px center'
                }}
              >
                <div className={`${planet.color} rounded-full shadow-lg relative group/planet`}>
                  <div 
                    className="rounded-full shadow-inner"
                    style={{
                      width: `${planet.size}px`,
                      height: `${planet.size}px`,
                      background: `radial-gradient(circle at 30% 30%, white/10, ${planet.color.replace('bg-', '')})`
                    }}
                  >
                    {/* Planet details */}
                    {planet.rings && (
                      <div className="absolute -inset-2 border-2 border-yellow-300/30 rounded-full" />
                    )}
                  </div>
                  
                  {/* Moon for Earth */}
                  {planet.moon && (
                    <div className="absolute -top-2 -right-2 w-2 h-2 bg-slate-300 rounded-full animate-orbit" />
                  )}

                  {/* Tooltip */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover/planet:opacity-100 transition-opacity duration-300">
                    <div className="px-2 py-1 bg-slate-900 text-white text-xs font-medium rounded whitespace-nowrap">
                      {planet.name}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Zoom Indicator */}
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm text-white text-xs font-medium rounded-full flex items-center gap-1.5">
              <Eye size={12} />
              {isZoomed ? "Click to zoom out" : "Click to zoom"}
            </div>
          </div>

          {/* Diagram Info */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-lg text-slate-900">Solar System Overview</h4>
              <div className="flex items-center gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="fill-current" />
                ))}
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Interactive visualization showing relative sizes, positions, and orbital paths of planets in our solar system.
            </p>
            
            {/* Stats */}
            <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded">
                  <Target size={14} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Planets</div>
                  <div className="text-sm font-semibold text-slate-900">6 shown</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded">
                  <Sparkles size={14} className="text-purple-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Accuracy</div>
                  <div className="text-sm font-semibold text-slate-900">98.7%</div>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Controls */}
          <div className="absolute top-4 right-4 flex gap-1">
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="p-2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-lg border border-slate-200 shadow-sm transition-all hover:scale-105"
              title={isZoomed ? "Zoom Out" : "Zoom In"}
            >
              {isZoomed ? (
                <Minimize2 size={16} className="text-slate-600" />
              ) : (
                <Maximize2 size={16} className="text-slate-600" />
              )}
            </button>
          </div>
        </div>

        {/* Insights Carousel */}
        {/* <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <h4 className="font-bold text-slate-900">AI Insights</h4>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setActiveInsight((prev) => (prev > 0 ? prev - 1 : insights.length - 1))}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronUp size={16} className="text-slate-600" />
              </button>
              <button
                onClick={() => setActiveInsight((prev) => (prev < insights.length - 1 ? prev + 1 : 0))}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronDown size={16} className="text-slate-600" />
              </button>
            </div>
          </div>

          Insights Container

          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div
                key={insight.id}
                className={`${insight.bgColor} rounded-2xl p-5 border transition-all duration-500 ${
                  activeInsight === index
                    ? "border-slate-200 shadow-sm scale-[1.02]"
                    : "border-transparent opacity-80"
                }`}
                onClick={() => setActiveInsight(index)}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${insight.color} shadow-sm`}>
                    <insight.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="font-bold text-slate-900 text-sm">{insight.title}</h5>
                      {activeInsight === index && (
                        <div className="px-2 py-0.5 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 text-xs font-medium rounded-full">
                          Active
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{insight.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          Dots Indicator
          
          <div className="flex justify-center gap-1.5 mt-4">
            {insights.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveInsight(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  activeInsight === index
                    ? "bg-gradient-to-r from-teal-500 to-cyan-500 w-6"
                    : "bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        </div> */}

        {/* Quick Actions */}
        {!isFullscreen && (
          <div className="sticky bottom-0 pt-4 bg-gradient-to-t from-white via-white to-transparent">
            <div className="bg-gradient-to-r from-slate-50 to-white/50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Need more details?</p>
                  <p className="text-xs text-slate-500">Generate additional visualizations</p>
                </div>
                <button className="px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white text-sm font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/25">
                  Generate More
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to { transform: translateY(-50%) rotate(360deg); }
        }
        @keyframes orbit {
          from { transform: rotate(0deg) translateX(var(--orbit-radius)) rotate(0deg); }
          to { transform: rotate(360deg) translateX(var(--orbit-radius)) rotate(-360deg); }
        }
        .animate-orbit {
          animation: orbit 5s linear infinite;
        }
      `}</style>
    </section>
  )
}